import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { container } from "tsyringe";

import type { AccessStorage } from "@/shared/libs/storage/AccessStorageImpl";

// behaviors
import { UserBehavior } from "@/entities/user/models/behaviors/UserBehavior";
import type { PrincipalTeacherDto, UserDto } from "@/entities/user/models/dtos";
import type { AuthState } from "@/entities/user/models/store/interface";
import type { UserType } from "@/entities/user/models/enums";

const useAuthenticationStore = defineStore(
    "authentication",
    () => {
        const authState = ref<AuthState>({
            userDto: null,
            userEmail: "",
            academyId: "",
            teacherId: "",
            userType: 0,
        });

        /**
         * @description
         * 학원의 이름을 전달합니다.
         */
        const academyName = computed(() => {
            const userBehavior = container.resolve(UserBehavior);

            if (!authState.value.userDto) return;

            return userBehavior.isPrincipal(authState.value.userDto)
                ? (authState.value.userDto as PrincipalTeacherDto).academy?.name
                : "수학에 심장을 달다";
        });

        /**
         * @description
         * 유저의 이메일을 전달합니다.
         */
        const setUserEmail = (userEmail: string) => {
            const accessStorage = container.resolve<AccessStorage>("AccessStorage");

            authState.value.userEmail = userEmail;
            accessStorage.setItem("userEmail", userEmail);
        };

        /**
         * @description
         * 유저의 정보를 저장합니다.
         */
        const setUserInfo = (userDto: UserDto) => {
            authState.value.userDto = userDto;
        };

        /**
         * @description
         * 원장님의 정보를 저장합니다.
         */
        const setPrincipalInfo = (academyId: string, userType: UserType) => {
            authState.value.academyId = academyId;
            authState.value.userType = userType;
        };

        /**
         * @description
         * 선생님의 정보를 저장합니다.
         */
        const setTeacherInfo = (academyId: string, teacherId: string, userType: UserType) => {
            authState.value.academyId = academyId;
            authState.value.teacherId = teacherId;
            authState.value.userType = userType;
        };

        /**
        /**
         * @description
         * 유저의 정보를 초기화합니다.
         */
        const resetState = () => {
            const accessStorage = container.resolve<AccessStorage>("AccessStorage");

            authState.value.userDto = null;
            authState.value.userEmail = "";
            authState.value.academyId = "";
            authState.value.teacherId = "";
            authState.value.userType = 0;

            accessStorage.removeItem("userEmail");
            accessStorage.removeItem("userAccessToken");
            accessStorage.removeItem("userRefreshToken");
        };

        return {
            authState,
            academyName,
            setUserInfo,
            setUserEmail,
            setPrincipalInfo,
            setTeacherInfo,
            resetState,
        };
    },
    {
        persist: true,
    },
);

export interface AuthenticationStore extends ReturnType<typeof useAuthenticationStore> {}

export default useAuthenticationStore;
